/**
 * Scenario-aware analysis hooks.
 *
 * `useAnalysis(key, fetchAction)` is the single accessor every analysis screen
 * uses instead of the old `useSelector(s => s.data.<key>)` + `has*` + lazy-fetch
 * triple. It reads the result for the ACTIVE scenario, resolves the has* flag
 * (from the project slice for base, from the data slice for scenarios), and owns
 * the lazy fetch. Screens don't need to know a scenario dimension exists.
 */
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { BASE } from "Redux/data/actions";

// result key -> has* flag name (same name is used for base and scenario flags).
const KEY_FLAG = {
  aging: "hasAging",
  rollforward: "hasRollforward",
  delinquencies: "hasDelinquencies",
  dilution: "hasDilution",
  loss: "hasLoss",
  yield: "hasYield",
  aggregateReserve: "hasAggregateReserve",
  buyerParent: "hasBuyerParent",
  concentrationLimits: "hasConcentrationLimits",
  nerb: "hasNerb",
};

/** The currently active scenario id ("base" for the original project). */
export const useActiveScenario = () =>
  useSelector((s) => s.data.activeScenario) || BASE;

/**
 * Resolve a has* flag for the active scenario.
 * Base flags live in the project slice; scenario flags in data.flags[sid].
 */
export const useScenarioFlag = (flag) => {
  const sid = useActiveScenario();
  return useSelector((s) =>
    sid === BASE ? s.project[flag] : s.data.flags?.[sid]?.[flag]
  );
};

/** Read a scenario-scoped result for the active scenario. */
export const useResult = (key) => {
  const sid = useActiveScenario();
  return useSelector((s) => s.data.results?.[sid]?.[key]);
};

/**
 * Scenario-aware analysis accessor.
 *   const { data: aging, has: hasAging, loading } = useAnalysis("aging", getAgingData);
 * Owns the lazy fetch: if the analysis exists on disk (has) but isn't loaded for
 * the active scenario, it fetches it against that scenario.
 */
export const useAnalysis = (key, fetchAction) => {
  const dispatch = useDispatch();
  const sid = useActiveScenario();
  const data = useResult(key);
  const has = useScenarioFlag(KEY_FLAG[key]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // `undefined` means "not fetched for this scenario"; a fetched-but-empty
    // result is still an object/array, so this won't refetch forever.
    if (has && data === undefined && fetchAction) {
      setLoading(true);
      dispatch(fetchAction(() => setLoading(false), sid));
    }
  }, [has, data, sid, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, has, loading, scenarioId: sid, isScenario: sid !== BASE };
};
